import React, { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { Blurhash } from 'react-blurhash'
import { useTranslation } from 'react-i18next'

import ImagePickerDialog from '@components/shared/ImagePicker/ImagePickerDialog'
import ImageCropDialog from '@components/shared/ImageUploader/ImageCropDialog'
import Tooltip from '@components/shared/Tooltip'
import { useDialogStore } from '@stores/dialog.store'
import { axiosInstance } from '@utils/http'

import type { UploadCollectionImageResponse } from '@services/CollectionService'

interface Props {
  imageUrl: string
  blurHash?: string | null
  labelKey: string
  overlayContent?: React.ReactNode
  className?: string
  aspect?: number
  onUpload: (resp: UploadCollectionImageResponse, blurHash: string | null) => Promise<boolean>
}

const ImageUploader: React.FC<Props> = ({
  imageUrl,
  blurHash: initialBlurHash,
  labelKey,
  overlayContent,
  className,
  aspect,
  onUpload,
}) => {
  const { t } = useTranslation()
  const openDialog = useDialogStore((s) => s.openDialog)

  const inputRef = useRef<HTMLInputElement>(null)
  const [showTippy, setShowTippy] = useState(false)

  const [imgSrc, setImgSrc] = useState('')
  const [blurHash, setBlurHash] = useState<string | null>(initialBlurHash ?? null)
  const [imgLoaded, setImgLoaded] = useState(false)

  useEffect(() => {
    setImgLoaded(false)
  }, [imageUrl])

  useEffect(() => {
    setBlurHash(initialBlurHash ?? null)
  }, [initialBlurHash])

  const doUpload = async (resp: UploadCollectionImageResponse) => {
    return onUpload(resp, blurHash)
  }

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader()
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() ?? '')
        openDialog('image_cropper')
      })
      reader.readAsDataURL(e.target.files[0])
    }
  }

  const onSelectImage = async (externalUrl: string, blurHash: string | null) => {
    setBlurHash(blurHash)

    const res = await axiosInstance.get(externalUrl, {
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    })

    if (res.data) {
      const reader = new FileReader()
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() ?? '')
        openDialog('image_cropper', { closeOthers: true })
      })
      reader.readAsDataURL(res.data)
    }
  }

  return (
    <>
      <Tooltip
        visible={showTippy}
        onHide={() => setShowTippy(false)}
        onClickOutside={() => setShowTippy(false)}
        interactive
        arrow
        placement="bottom"
        content={
          <div className="py-1 text-sm">
            <button
              type="button"
              className="mb-2 block cursor-pointer hover:opacity-90"
              onClick={(e) => {
                e.preventDefault()
                inputRef.current?.click()
                setShowTippy(false)
              }}>
              {t('action.upload')}
            </button>
            <button
              type="button"
              className="block cursor-pointer hover:opacity-90"
              onClick={(e) => {
                e.preventDefault()
                openDialog('image_picker')
                setShowTippy(false)
              }}>
              {t('action.chooseStockPhoto')}
            </button>
          </div>
        }>
        <div
          className={classNames(
            'w-full relative h-full flex flex-row items-center overflow-hidden rounded',
            className
          )}>
          <img
            src={imageUrl}
            className={classNames(
              imgLoaded ? 'w-full min-h-[280px] opacity-100 object-cover' : 'opacity-0'
            )}
            alt="Image uploader"
            onLoad={() => setImgLoaded(true)}
          />
          <div
            className={classNames(
              'absolute inset-0 transition-opacity min-h-[280px]',
              imgLoaded ? 'opacity-0' : 'opacity-100'
            )}>
            {blurHash && <Blurhash hash={blurHash} width="100%" height={280} />}
          </div>
          <button
            type="button"
            onClick={() => setShowTippy(true)}
            className="absolute inset-0 z-20 flex cursor-pointer flex-row items-center justify-center bg-black bg-opacity-0 text-white duration-300 group transition-bg-opacity hover:bg-opacity-50 text-md">
            <i className="mr-1 nf-icon nf-camera opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {t(labelKey)}
            </span>
          </button>
          {overlayContent}

          <input
            type="file"
            ref={inputRef}
            className="hidden"
            accept="image/png,image/jpeg"
            onChange={onSelectFile}
            multiple={false}
          />
        </div>
      </Tooltip>
      <ImageCropDialog imgSrc={imgSrc} aspect={aspect ?? 1.0} onUpload={doUpload} />
      <ImagePickerDialog onComplete={onSelectImage} />
    </>
  )
}

export default ImageUploader
